import { Component, DestroyRef, OnDestroy, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { LatestInsights } from '../../../shared/latest-insights/latest-insights';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';
import { catchError, of } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import { FrontendEvent } from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';

interface EventView {
  id: string;
  name: string;
  uppercaseName: string;
  routeLink: string[];
  startTimeMs: number | null;
  endTimeMs: number | null;
  dateLabel: string;
  monthLabel: string;
  thumbnailBackgroundImage: string | null;
  iconHtml: SafeHtml | null;
  iconImageUrl: string;
  couponCount: number;
}

interface CountdownParts {
  days: string;
  hours: string;
  mins: string;
  secs: string;
}

@Component({
  selector: 'app-events-page',
  imports: [Breadcrumb, RouterLink, LatestInsights, DevFlag],
  templateUrl: './events-page.html',
  styleUrl: './events-page.scss',
})
export class EventsPage implements OnDestroy {
  protected readonly crumbs = breadcrumbTrail({ label: 'Events' });

  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly seo = inject(SeoService);

  protected readonly events = signal<EventView[]>([]);
  protected readonly isLoadingEvents = signal(true);
  protected readonly now = signal(Date.now());
  protected readonly expandedFaqIndex = signal<number | null>(null);

  protected readonly activeEvents = computed(() => {
    const now = this.now();

    return this.events().filter(
      (event) =>
        event.startTimeMs !== null &&
        event.endTimeMs !== null &&
        event.startTimeMs <= now &&
        event.endTimeMs >= now,
    );
  });

  protected readonly upcomingEvents = computed(() => {
    const now = this.now();

    return this.events().filter((event) => event.startTimeMs !== null && event.startTimeMs > now);
  });

  protected readonly featuredUpcomingEvents = computed(() => this.upcomingEvents().slice(0, 6));
  protected readonly moreUpcomingEvents = computed(() => this.upcomingEvents().slice(6));
  protected readonly archiveEvents = computed(() => {
    const nowDate = new Date(this.now());
    const currentYear = nowDate.getFullYear();

    return this.events()
      .filter((event) => {
        if (event.endTimeMs === null || event.endTimeMs >= nowDate.getTime()) {
          return false;
        }

        return new Date(event.endTimeMs).getFullYear() === currentYear;
      })
      .sort((a, b) => (b.endTimeMs ?? 0) - (a.endTimeMs ?? 0));
  });

  private timerId: ReturnType<typeof setInterval>;

  constructor() {
    this.applyEventsSeo();
    this.loadEvents();
    this.timerId = setInterval(() => this.now.set(Date.now()), 1000);
  }

  ngOnDestroy(): void {
    clearInterval(this.timerId);
  }

  /**
   * SEO for the events calendar. The page's copy is static (the event list only fills the sections
   * below the fold), so nothing here waits on the API and the constructor is the right place.
   */
  private applyEventsSeo(): void {
    const path = '/events';
    const url = this.seo.absolute(path);
    const title = 'Sales & Shopping Events Calendar | SavingArena';
    const description =
      'Every major sales event we track — Black Friday, Cyber Monday, Prime Day and more — with the dates, the stores taking part and their verified coupons and deals.';

    this.seo.apply({ title, description, canonicalPath: path });
    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  protected toggleFaq(index: number): void {
    this.expandedFaqIndex.update((current) => (current === index ? null : index));
  }

  protected countdownParts(targetTimeMs: number | null): CountdownParts {
    const diff = Math.max(0, (targetTimeMs ?? this.now()) - this.now());

    return {
      days: this.formatTime(Math.floor(diff / (1000 * 60 * 60 * 24))),
      hours: this.formatTime(Math.floor((diff / (1000 * 60 * 60)) % 24)),
      mins: this.formatTime(Math.floor((diff / 1000 / 60) % 60)),
      secs: this.formatTime(Math.floor((diff / 1000) % 60)),
    };
  }

  protected daysUntilLabel(startTimeMs: number | null): string {
    if (startTimeMs === null) {
      return 'Date TBA';
    }

    const diffDays = Math.ceil((startTimeMs - this.now()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return 'Live Now';
    }

    return diffDays === 1 ? '1 Day Left' : `${diffDays} Days Left`;
  }

  protected eventMeta(status: 'Upcoming' | 'Concluded', event: EventView): string {
    const couponText = event.couponCount > 0 ? ` • ${event.couponCount.toLocaleString()} Coupons` : '';

    return `${status}${couponText}`;
  }

  private loadEvents(page = 1, accumulatedEvents: FrontendEvent[] = []): void {
    this.api
      .getEvents({ page, limit: 100 })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response) {
          this.events.set(this.toEventViews(accumulatedEvents));
          this.isLoadingEvents.set(false);
          return;
        }

        const nextEvents = [...accumulatedEvents, ...response.data];

        if (response.pagination.page < response.pagination.pages) {
          this.loadEvents(response.pagination.page + 1, nextEvents);
          return;
        }

        this.events.set(this.toEventViews(nextEvents));
        this.isLoadingEvents.set(false);
      });
  }

  private toEventViews(events: FrontendEvent[]): EventView[] {
    return events
      .map((event) => {
        const startTimeMs = this.parseDateMs(event.startDate);
        const endTimeMs = this.parseDateMs(event.endDate);

        return {
          id: event.id,
          name: event.name,
          uppercaseName: event.name.toUpperCase(),
          routeLink: ['/event', event.slug || event.id],
          startTimeMs,
          endTimeMs,
          dateLabel: this.dateLabel(startTimeMs),
          monthLabel: this.monthLabel(startTimeMs),
          thumbnailBackgroundImage: this.thumbnailBackgroundImage(event.thumbnailImagePath),
          iconHtml: this.apiIconHtml(event),
          iconImageUrl: this.apiIconImageUrl(event),
          couponCount: this.countFromUnknown(event.coupons),
        } satisfies EventView;
      })
      .sort((a, b) => (a.startTimeMs ?? Number.MAX_SAFE_INTEGER) - (b.startTimeMs ?? Number.MAX_SAFE_INTEGER));
  }

  private parseDateMs(value?: string): number | null {
    if (!value) {
      return null;
    }

    const dateMs = new Date(value).getTime();

    return Number.isNaN(dateMs) ? null : dateMs;
  }

  private dateLabel(timeMs: number | null): string {
    if (timeMs === null) {
      return 'DATE TBA';
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).formatToParts(new Date(timeMs));
    const day = parts.find((part) => part.type === 'day')?.value ?? '';
    const month = parts.find((part) => part.type === 'month')?.value.toUpperCase() ?? '';
    const year = parts.find((part) => part.type === 'year')?.value ?? '';

    return `${day} ${month} ${year}`.trim();
  }

  private monthLabel(timeMs: number | null): string {
    if (timeMs === null) {
      return 'TBA';
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).formatToParts(new Date(timeMs));
    const month = parts.find((part) => part.type === 'month')?.value.toUpperCase() ?? '';
    const year = parts.find((part) => part.type === 'year')?.value ?? '';

    return `${month} ${year}`.trim();
  }

  private thumbnailBackgroundImage(path?: string): string | null {
    if (!path) {
      return null;
    }

    return `linear-gradient(rgb(11 20 56 / 32%), rgb(11 20 56 / 32%)), url('${this.assetUrl(path)}')`;
  }

  private apiIconHtml(event: FrontendEvent): SafeHtml | null {
    if (event.iconType !== 'code' || !event.iconCode?.trim()) {
      return null;
    }

    return this.sanitizer.bypassSecurityTrustHtml(event.iconCode);
  }

  private apiIconImageUrl(event: FrontendEvent): string {
    if (event.iconType === 'code' || !event.iconImagePath) {
      return '';
    }

    return this.assetUrl(event.iconImagePath);
  }

  private assetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private countFromUnknown(value: unknown): number {
    if (Array.isArray(value)) {
      return value.length;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    return 0;
  }

  private formatTime(value: number): string {
    return value.toString().padStart(2, '0');
  }
}
